import { MapIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '../../../../components/Button';
import { HideSelect, HideSelectorProps } from '../../../../components/HideSelector';
import { SelectItemContent } from '../../../../components/selects/SelectItem';
import { RendererReadOnlyContext } from '../../../../contexts/RendererReadOnlyContext';
import { UserContext } from '../../../../contexts/UserContext';
import useOpen from '../../../../hooks/useOpen';
import { selectMap } from '../../../../redux/rendererReducer';
import { useCustomSelector } from '../../../../redux/store';
import { T } from '../../../../translation/src';
import { PointCloud, Project } from '../../../../types/graphqlTypes';
import { ProjectProjectionSystemModal, ProjectProjectionSystemModalProps } from '../../../project/ProjectProjectionSystemModal';
import { UploadPointCloudModal } from '../../../project/UploadPointCloudModal';
import { useHasOrthographicView } from '../../../../hooks/potree/useHasOrthographicView';
import { useCADObjects } from '../../../../hooks/potree/useRenderer';
import { CadLayers } from './CadLayers';
import { AddCADLayerModal } from '../../../project/AddCADLayerModal';
import { WMSLayerModal, WMSLayerModalProps } from '../../../project/WMSLayerModal';
import { WmsLayers } from './WmsLayers';
import { useWmsLayers } from '../../../../hooks/potree/useWmsLayers';
import { OrthophotoLayerModal } from '../../../project/AddOrthophotoLayerModal';
import { useOrthophotoLayers } from '../../../../hooks/potree/useOrthophotoLayers';
import { OrthophotoLayers } from './OrthophotoLayers';
import { PointCloudLayers } from './PointCloudLayers';
import { useViewManager } from '../../../../hooks/useViewManager';
import { useToggleHide } from '../../../../hooks/modules/project/useToggleHide';

interface LayersProps {
  project?: Pick<Project, 'settings' | 'id' | 'mapVisible'> & {
    pointClouds: Pick<PointCloud, 'cloudName' | 'id' | 'visible' | 'displayName' | 'uploadStatus' | 'availableClasses'>[];
  };
}
const Layers_: React.FC2<LayersProps> = ({ project }) => {
  const pointClouds = project?.pointClouds || [];
  const dispatch = useDispatch();
  const isReadOnly = useContext(RendererReadOnlyContext);
  const { onClose: closeMapModal, open: mapModalOpen, onOpenWithValue, value: mapModalValue } = useOpen<{ origin: 'MAP' | 'WMS' | 'ORTHOPHOTO' | 'CAD' }>();
  const { onClose: closeCADLayerModal, onOpen: openCADLayerModal, open: CADLayerModalOpen } = useOpen();
  const { onClose: closeWmsLayerModal, onOpen: openWmsLayerModal, open: wmsLayerModalOpen, onOpenWithValue: openWmsLayerModalWithValue, value: wmsLayerModalValue } = useOpen<WMSLayerModalProps['initialValues']>();
  const { onClose: closeOrthophotoLayerModal, onOpen: openOrthophotoLayerModal, open: orthophotoLayerModalOpen } = useOpen();
  const { onClose: closePointCloudUploadModal, onOpen: openPointCloudUploadModal, open: pointCloudUploadModalOpen } = useOpen();
  const { mapSelected } = useCustomSelector((state) => state.rendererProvider, ['selectedPointCloudName', 'mapSelected']);
  const currentUser = useContext(UserContext);
  const mapVisible = project?.mapVisible ?? false;
  const [{ cadLayers }] = useCADObjects();
  const { wmsLayers } = useWmsLayers();
  const { orthophotoLayers } = useOrthophotoLayers();

  const hasOrthographicView = useHasOrthographicView();
  const { enable2DMode } = useViewManager();
  const { toggleHide } = useToggleHide();
  const projectionSystem = project?.settings?.projectionSystem;

  const toggleMapVisibility = useCallback(() => {
    if (!mapVisible) enable2DMode('map');
    toggleHide({ identifiers: ['map'], visible: !mapVisible });
  }, [mapVisible, enable2DMode, toggleHide]);

  const onToggleHideMap: Required<HideSelectorProps>['onClick'] = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (projectionSystem) {
        toggleMapVisibility();
      } else {
        onOpenWithValue({ origin: 'MAP' });
      }
    },
    [projectionSystem, toggleMapVisibility, onOpenWithValue],
  );

  const onClickMap = useCallback(() => {
    return dispatch(selectMap({}));
  }, [dispatch]);

  const onMapModalSuccess: Required<ProjectProjectionSystemModalProps>['onSuccess'] = useCallback(
    (event) => {
      if (mapModalValue?.origin === 'MAP') toggleMapVisibility();
      else if (mapModalValue?.origin === 'WMS') openWmsLayerModal();
      else if (mapModalValue?.origin === 'ORTHOPHOTO') openOrthophotoLayerModal();
      else if (mapModalValue?.origin === 'CAD') openCADLayerModal();
      closeMapModal();
    },
    [closeMapModal, mapModalValue?.origin, toggleMapVisibility, openWmsLayerModal, openOrthophotoLayerModal, openCADLayerModal],
  );

  const onOpenWmsLayerModal = useCallback(() => {
    if (projectionSystem) return openWmsLayerModal();
    return onOpenWithValue({ origin: 'WMS' });
  }, [onOpenWithValue, openWmsLayerModal, projectionSystem]);

  const onOpenOrthophotoLayerModal = useCallback(() => {
    if (projectionSystem) return openOrthophotoLayerModal();
    return onOpenWithValue({ origin: 'ORTHOPHOTO' });
  }, [onOpenWithValue, openOrthophotoLayerModal, projectionSystem]);

  const onOpenCadLayerModal = useCallback(() => {
    if (projectionSystem) return openCADLayerModal();
    return onOpenWithValue({ origin: 'CAD' });
  }, [onOpenWithValue, openCADLayerModal, projectionSystem]);

  return (
    <>
      <div className="flex flex-col flex-grow mt-8 overflow-auto">
        <div className="flex flex-col overflow-auto divide-y divide-gray-800">
          {pointClouds.length ? <PointCloudLayers pointClouds={pointClouds}></PointCloudLayers> : null}
          <div className="flex flex-col py-2">
            <SelectItemContent icon={MapIcon} idle={!mapVisible} selectStyle="primary" onClick={onClickMap} active={mapSelected} className="pl-8">
              <T _str="map" swc />
              {hasOrthographicView && (
                <div className="flex justify-end flex-grow">
                  <HideSelect hidden={!mapVisible} onClick={onToggleHideMap} />
                </div>
              )}
            </SelectItemContent>
          </div>
          {cadLayers.length ? <CadLayers className="flex flex-col" cadLayers={cadLayers} /> : null}
          {wmsLayers.length ? <WmsLayers className="flex flex-col" wmsLayers={wmsLayers} onUpdateLayer={openWmsLayerModalWithValue} /> : null}
          {orthophotoLayers.length ? <OrthophotoLayers className="flex flex-col" orthophotoLayers={orthophotoLayers} /> : null}
        </div>

        <div className="flex items-end flex-grow w-full">
          {!isReadOnly && (
            <div className="w-full">
              {/* Adding point clouds to an existing project is superadmin-only; server enforces via @auth(superAdmin: true) */}
              {currentUser.isSuperAdmin && (
                <Button variant="secondary" onClick={openPointCloudUploadModal} className="w-full mt-2" size="sm">
                  <T _str="add pointcloud" swc />
                </Button>
              )}
              <Button variant="secondary" onClick={onOpenCadLayerModal} className="w-full mt-2" size="sm">
                <T _str="add CAD file" swc />
              </Button>
              <Button variant="secondary" onClick={onOpenWmsLayerModal} className="w-full" size="sm">
                <T _str="add WM(T)S" swc />
              </Button>
              <Button variant="secondary" onClick={onOpenOrthophotoLayerModal} className="w-full" size="sm">
                <T _str="add orthophoto" swc />
              </Button>
            </div>
          )}
        </div>
      </div>
      {project && (
        <ProjectProjectionSystemModal
          open={mapModalOpen}
          onClose={closeMapModal}
          project={project}
          onSuccess={onMapModalSuccess}
          title={
            mapModalValue?.origin === 'WMS' ? (
              <T _str="first select a projection system before adding a wms layer" swc />
            ) : mapModalValue?.origin === 'ORTHOPHOTO' ? (
              <T _str="first select a projection system before adding an orthophoto" swc />
            ) : null
          }
        />
      )}
      {currentUser.isSuperAdmin && <UploadPointCloudModal onClose={closePointCloudUploadModal} open={pointCloudUploadModalOpen} />}

      <AddCADLayerModal open={CADLayerModalOpen} onClose={closeCADLayerModal} />
      <WMSLayerModal open={wmsLayerModalOpen} onClose={closeWmsLayerModal} initialValues={wmsLayerModalValue} />
      <OrthophotoLayerModal open={orthophotoLayerModalOpen} onClose={closeOrthophotoLayerModal} />
    </>
  );
};

export const Layers = memo(Layers_);
